import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import Typography from "@mui/material/Typography";
import { CircularProgress, Grid, Button } from "@mui/material";
import CommonBody from "components/Modules/CommonBody";
import TextAreaInputField from "components/Elements/TextAreaInputField";
import TabTitle from "components/Elements/TabTitle";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import {
	getSiteApplicationDetail,
	patchApplicationDetail,
} from "services/clients/sites/siteApplications/siteApplicationDetails";

const useStyles = makeStyles()((theme) => ({
	labelText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "18px",
		marginBottom: "8px",
	},
	helperText: {
		fontFamily: "Roboto Condensed",
		fontSize: "14px",
		color: "#666",
		marginBottom: "12px",
	},
	clearButton: {
		marginTop: "12px",
	},
	formContainer: {
		padding: "20px",
		maxWidth: "900px",
	},
}));

const DefectTemplateContent = ({ id, state, dispatch, setIs404, getError }) => {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const errorDispatch = useDispatch();
	const [defectTemplate, setDefectTemplate] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const [loading, setLoading] = useState(true);
	const [haveData, setHaveData] = useState(false);

	const {
		details: { data },
		defaultCustomCaptionsData: { defect, defectPlural },
		isReadOnly,
	} = state;

	const fetchSiteApplicationDetails = async () => {
		try {
			const result = await getSiteApplicationDetail(id);

			if (result.status) {
				setDefectTemplate(result.data.defectTemplate || "");
				setLoading(false);
				setHaveData(true);
			} else if (result.status === 404) {
				setIs404(true);
			} else {
				throw new Error(result);
			}
		} catch (err) {
			errorDispatch(showError(t("message.failedToLoadDefectTemplateSettings")));
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSiteApplicationDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const handleTemplateUpdate = async (value) => {
		setIsUpdating(true);
		try {
			const result = await patchApplicationDetail(id, [
				{
					op: "replace",
					path: "defectTemplate",
					value: value || null,
				},
			]);

			if (result.status) {
				await fetchSiteApplicationDetails();
			} else {
				getError(
					result?.data?.detail || t("message.couldNotUpdateDefectTemplate")
				);
				// Revert to original value on error
				await fetchSiteApplicationDetails();
			}
		} catch (err) {
			errorDispatch(showError(t("message.failedToUpdateDefectTemplate")));
		}
		setIsUpdating(false);
	};

	const onTextAreaChange = (e) => {
		setDefectTemplate(e.target.value);
	};

	const handleClearTemplate = async () => {
		setDefectTemplate("");
		await handleTemplateUpdate("");
	};

	return (
		<div className="container">
			<TabTitle
				title={`${data.application.name} ${
					data?.defectTemplateCC || "Defect Template"
				}`}
			/>
			<CommonBody haveData={haveData}>
				<>
					{loading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<CircularProgress />
						</div>
					) : (
						<div className={classes.formContainer}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Typography className={classes.labelText}>
										{t("label.entityDetailsTemplate", {
											entity: data?.defectCC || defect,
										})}
									</Typography>
									<Typography className={classes.helperText}>
										{t("message.defectTemplateHelperText", {
											defect:
												data?.defectCC?.toLowerCase() || defect.toLowerCase(),
											defectPlural:
												data?.defectPluralCC?.toLowerCase() ||
												defectPlural.toLowerCase(),
										})}
									</Typography>
									<TextAreaInputField
										value={defectTemplate}
										minRows={6}
										onChange={onTextAreaChange}
										onBlur={() => handleTemplateUpdate(defectTemplate)}
										disabled={isReadOnly || isUpdating}
										placeholder={t(
											"placeholder.enterTemplateTextForEntityDetails",
											{
												entity:
													data?.defectCC?.toLowerCase() || defect.toLowerCase(),
											}
										)}
										style={{ fontSize: "14px", resize: "none" }}
									/>
								</Grid>
								<Grid item xs={12}>
									<Button
										variant="outlined"
										color="primary"
										onClick={handleClearTemplate}
										disabled={isReadOnly || isUpdating || !defectTemplate}
										className={classes.clearButton}
									>
										{t("action.clearTemplate")}
									</Button>
								</Grid>
							</Grid>
						</div>
					)}
				</>
			</CommonBody>
		</div>
	);
};

export default DefectTemplateContent;
